// Large histories live in IndexedDB. The synchronous engine writes through a small
// localStorage journal first, so even closing the tab before an IDB commit loses no answer.
// Existing keys are copied before removal. On IDB failure the journal remains readable.
import { browserStore } from './storage.js';
const PREFIX = 'sentenzo.journal.';
const owned = k => /^sentenzo\.(app\.|sync\.|v2\.|device)/.test(k);
export async function durableBrowserStore({ls=globalThis.localStorage,idb=globalThis.indexedDB}={}) {
  const fallback=browserStore(ls);
  if (!fallback.persistent || !idb) {
    if(ls?.getItem('sentenzo.idb.initialized'))throw new Error('Lernspeicher nicht verfügbar');
    return fallback;
  }
  let db;
  try {
    db = await new Promise((resolve,reject) => {
      const r=idb.open('sentenzo-learning',1);
      r.onupgradeneeded=()=>r.result.createObjectStore('kv');
      r.onsuccess=()=>resolve(r.result);r.onerror=()=>reject(r.error);
      r.onblocked=()=>reject(new Error('IndexedDB blocked'));
    });
  } catch(error) { if(ls.getItem('sentenzo.idb.initialized'))throw error; return fallback; }
  const cache=new Map();
  await new Promise((resolve,reject)=>{
    const tx=db.transaction('kv','readonly'), r=tx.objectStore('kv').openCursor();
    r.onsuccess=()=>{const c=r.result;if(c){cache.set(c.key,c.value);c.continue();}};
    tx.oncomplete=resolve;tx.onerror=()=>reject(tx.error);
  });
  const jobs=new Set();
  function commit(k,v,journalValue,legacyValue) {
    const job=new Promise(resolve=>{
      const tx=db.transaction('kv','readwrite'), s=tx.objectStore('kv');
      if(v===null)s.delete(k);else s.put(v,k);
      tx.oncomplete=()=>{
        if(journalValue!==undefined && ls.getItem(PREFIX+k)===journalValue) ls.removeItem(PREFIX+k);
        if(legacyValue!==undefined && ls.getItem(k)===legacyValue) ls.removeItem(k);
        resolve(true);
      };
      tx.onerror=tx.onabort=()=>resolve(false); // synchronous journal is still durable
    });
    jobs.add(job);job.finally(()=>jobs.delete(job));return job;
  }
  const keys=Array.from({length:ls.length},(_,i)=>ls.key(i));
  // Replay legacy snapshots first, then any interrupted writes (including tombstones).
  for(const k of keys.filter(owned)) {const v=ls.getItem(k);cache.set(k,v);await commit(k,v,undefined,v);}
  for(const jk of keys.filter(k=>k.startsWith(PREFIX))) {
    const raw=ls.getItem(jk), k=jk.slice(PREFIX.length), v=JSON.parse(raw);
    if(v===null)cache.delete(k);else cache.set(k,v);
    await commit(k,v,raw);
  }
  ls.setItem('sentenzo.idb.initialized','1');
  function write(k,v) {
    if(!owned(k)) {if(v===null)fallback.remove(k);else fallback.set(k,v);return;}
    const raw=JSON.stringify(v);
    ls.setItem(PREFIX+k,raw); // may throw: caller must NOT advance
    if(v===null)cache.delete(k);else cache.set(k,v);
    commit(k,v,raw);
  }
  return {persistent:true,kind:'indexeddb',get:k=>owned(k)?(cache.get(k)??null):fallback.get(k),
    set:(k,v)=>write(k,String(v)),remove:k=>write(k,null),
    keys:(prefix='')=>[...new Set([...cache.keys(),...fallback.keys().filter(k=>!k.startsWith(PREFIX))])].filter(k=>k.startsWith(prefix)),
    flush:()=>Promise.all([...jobs]),close:()=>db.close()};
}
